import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '../../../hooks';
import { generateRandomMobileNumber, generateRandomStudentId, hasDollarInString, lazyRetry } from '../../../utils/utils';
import { toast } from 'react-toastify';
import PaymentModes from '../PaymentModes/PaymentModes';
import PaymentForm from '../PaymentForm/PaymentForm';
import { gqSvcPaymentPageApiClient } from '../../../api-client/api-svcpp-client/axios';
import { LogoLoader, SpinnerLoader, ErrorBoundary } from '../../atoms';
import classes from './PaymentSection.module.scss';
import { ANALYTICS_EVENTS } from '../../../utils/constants';
import { sendAnalytics } from '../../../analytics';

// lazy loaded components
const CheckboxBlock = lazy(() => lazyRetry(() => import('../CheckboxBlock/CheckboxBlock'), '../CheckboxBlock/CheckboxBlock'));
const DynamicBlock = lazy(() => lazyRetry(() => import('../DynamicBlock/DynamicBlock'), '../DynamicBlock/DynamicBlock'));
const PaymentModesEdit = lazy(() => lazyRetry(() => import('../PaymentModesEdit/PaymentModesEdit'), '../PaymentModesEdit/PaymentModesEdit'));
const ReverseApiFlow = lazy(() => lazyRetry(() => import('../ReverseApiFlow/ReverseApiFlow'), '../ReverseApiFlow/ReverseApiFlow'));

const PROCESS_SLUGS = {
    REVERSE_API: 'reverse_api',
    CUSTOM_FORM: 'custom_form',
    CHECKBOX: 'checkbox',
    DYNAMIC: 'dynamic',
    EDIT_FORM: 'edit_form',
    PAYMENT_CARDS: 'payment_cards',
}

const PaymentSection = (props) => {
    const { data, sessionData } = props || {};
    // hooks/misc
    const query = useQuery();
    const paymentSectionRef = useRef();
    const params = useParams();
    const dispatch = useDispatch();
    // States
    const [activeStep, setActiveStep] = useState(PROCESS_SLUGS?.REVERSE_API);
    const [studentId, setStudentId] = useState(query.get('sid') || generateRandomStudentId(10));
    const [customerNumber, setCustomerNumber] = useState(query.get('m') || generateRandomMobileNumber());
    const [sbdCode, setSbdCode] = useState('');
    const [notes, setNotes] = useState(null);
    const [reverseApiData, setReverseApiData] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [studentBasicInfoData, setStudentBasicInfoData] = useState(null);
    const [checkboxSessionCode, setCheckboxSessionCode] = useState('');
    const [dynamicFtSessionCode, setDynamicFtSessionCode] = useState('');
    // 
    const [hasReverseApiFlow, setHasReverseApiFlow] = useState(false);
    const [hasCustomForm, setHasCustomForm] = useState(false);
    const [hasEditFlow, setHasEditFlow] = useState(false);
    const [allFeeGroups, setAllFeeGroups] = useState([]);
    const [paymentPageFetchData, setPaymentPageFetchData] = useState(null);
    //
    const [isApiLoading, setIsApiLoading] = useState(true);
    const [workflowCode, setWorkflowCode] = useState('');

    const handleActiveStepUpdate = (active_slug) => {
        const { REVERSE_API, CUSTOM_FORM, CHECKBOX, DYNAMIC, EDIT_FORM, PAYMENT_CARDS } = PROCESS_SLUGS;
        if (![REVERSE_API, CUSTOM_FORM, CHECKBOX, DYNAMIC, EDIT_FORM, PAYMENT_CARDS].includes(active_slug)) {
            toast.error('Oops! Something went wrong. Please contact support@grayquest.com');
            return;
        }
        setActiveStep(active_slug);
    }

    const handleDataUpdate = (data) => {
        if (!data) return;
        if (data?.student_uuid) setStudentId(data?.student_uuid?.trim());
        if (data?.customer_mobile) setCustomerNumber(data?.customer_mobile);
        if (data?.sbd_code) setSbdCode(data?.sbd_code);
        if (data?.notes) setNotes(data?.notes);
        if (data?.reverse_api_data) setReverseApiData(data?.reverse_api_data);
        if (data?.selected_data) setSelectedData(data?.selected_data);
        if (data?.workflow_code) setWorkflowCode(data?.workflow_code);
        if (data?.checkbox_session_code) setCheckboxSessionCode(data?.checkbox_session_code);
        if (data?.dynamic_ft_session_code) setDynamicFtSessionCode(data?.dynamic_ft_session_code);
    }
    
    const renderActiveStep = (active_slug) => {
        switch (active_slug) {
            case PROCESS_SLUGS?.REVERSE_API:
                return (
                    <ErrorBoundary>
                        <Suspense fallback={<div className={classes.FullPageLoader}>
                                                <LogoLoader />
                                            </div>}>
                            <ReverseApiFlow 
                                data={data} 
                                studentId={studentId}
                                customerNumber={customerNumber}
                                sbdCode={sbdCode}
                                handleDataUpdate={handleDataUpdate}
                                handleActiveStepUpdate={handleActiveStepUpdate}
                                PROCESS_SLUGS={PROCESS_SLUGS}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            case PROCESS_SLUGS?.CUSTOM_FORM:
                return (<PaymentForm 
                        data={data} 
                        studentId={studentId}
                        customerNumber={customerNumber}
                        sbdCode={sbdCode}
                        handleDataUpdate={handleDataUpdate}
                        handleActiveStepUpdate={handleActiveStepUpdate}
                        PROCESS_SLUGS={PROCESS_SLUGS}
                        setStudentBasicInfoData={setStudentBasicInfoData}
                        //
                        hasReverseApiFlow={hasReverseApiFlow}
                        setPaymentPageFetchData={setPaymentPageFetchData}
                    />)
            case PROCESS_SLUGS?.CHECKBOX:
                return (
                    <ErrorBoundary>
                        <Suspense fallback={<div className={classes.FullPageLoader}>
                                                <LogoLoader />
                                            </div>}>
                            <CheckboxBlock 
                                data={data}
                                sbdCode={sbdCode}
                                studentId={studentId}
                                customerNumber={customerNumber}
                                handleActiveStepUpdate={handleActiveStepUpdate}
                                PROCESS_SLUGS={PROCESS_SLUGS}
                                setPaymentPageFetchData={setPaymentPageFetchData}
                                handleDataUpdate={handleDataUpdate}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            case PROCESS_SLUGS?.DYNAMIC:
                return (
                    <ErrorBoundary>
                        <Suspense fallback={<div className={classes.FullPageLoader}>
                                                <LogoLoader />
                                            </div>}>
                            <DynamicBlock 
                                data={data}
                                sbdCode={sbdCode}
                                handleActiveStepUpdate={handleActiveStepUpdate}
                                PROCESS_SLUGS={PROCESS_SLUGS}
                                studentId={studentId}
                                customerNumber={customerNumber}
                                setPaymentPageFetchData={setPaymentPageFetchData}
                                handleDataUpdate={handleDataUpdate}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            case PROCESS_SLUGS?.EDIT_FORM:
                return (
                    <ErrorBoundary>
                        <Suspense fallback={<div className={classes.FullPageLoader}>
                                                <LogoLoader />
                                            </div>}>
                            <PaymentModesEdit 
                                data={data}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            case PROCESS_SLUGS?.PAYMENT_CARDS:
                return (<PaymentModes 
                        data={paymentPageFetchData || data} 
                        studentId={studentId}
                        customerNumber={customerNumber}
                        sbdCode={sbdCode}
                        handleDataUpdate={handleDataUpdate}
                        // handleActiveStepUpdate={handleActiveStepUpdate}
                        // PROCESS_SLUGS={PROCESS_SLUGS}
                        hasCustomForm={hasCustomForm}
                        hasReverseApiFlow={hasReverseApiFlow}
                        reverseApiData={reverseApiData}
                        selectedData={selectedData}
                        notes={notes}
                        studentBasicInfoData={studentBasicInfoData}
                        workflowCode={workflowCode}
                        checkboxSessionCode={checkboxSessionCode}
                        dynamicFtSessionCode={dynamicFtSessionCode}
                    />)
            default:
                break;
        }
    }

    // Fetch session data by code
    const fetchSessionData = async (code) => {
        return await gqSvcPaymentPageApiClient({
            method: 'GET',
            url: `/v1/misc/session/fetch?session_code=${code}`,
        })
    }

    const fetchInitalPageData = async () => {
        try {
            setIsApiLoading(true);
            let feeGroupList = [];
            let pagehasEditFlow = false;

            if (sessionData) {
                const { payment_page_slug, customer_mobile, student_uuid, sbd_code, selected_data } = sessionData || {};
                const { notes } = selected_data || {};
                handleDataUpdate({
                    ...(student_uuid && { student_uuid: student_uuid?.toString() }),
                    ...(customer_mobile && { customer_mobile: customer_mobile.toString() }),
                    ...(sbd_code && { sbd_code: sbd_code }),
                    ...(notes && { notes: notes }),
                    ...(selected_data && { selected_data: selected_data }),
                });
            }

            if (data?.sdk_block?.length > 0) {
                data.sdk_block.map((fmode) => {
                    // if (!pagehasEditFlow && fmode?.fee_group?.is_editable) {
                    //     pagehasEditFlow=true;
                    // }
                    feeGroupList.push(fmode?.fee_group?.code);
                });
            }
            setAllFeeGroups([...feeGroupList]);
            // setHasEditFlow(pagehasEditFlow);

            paymentSectionRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'center',
            });

            if (data?.callback_config?.action && data?.callback_config?.fields) {
                setHasReverseApiFlow(true);
                setActiveStep(PROCESS_SLUGS?.REVERSE_API);
            } else if (data?.form_block?.visible && data?.form_block?.data?.fields?.length > 0) {
                setHasCustomForm(true);
                setActiveStep(PROCESS_SLUGS?.CUSTOM_FORM);
            } else if (data?.is_checkbox_enabled) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_SKIP_CUSTOM_FORM,
                    },
                });
                setActiveStep(PROCESS_SLUGS?.CHECKBOX);
            } else if (!hasReverseApiFlow && pagehasEditFlow) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_SKIP_CUSTOM_FORM,
                    },
                });
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_SKIP_FEE_HEADER_SELECTION,
                    },
                });
                setActiveStep(PROCESS_SLUGS?.EDIT_FORM);
            } else if (data?.sdk_block && data?.sdk_block?.length > 0) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_SKIP_CUSTOM_FORM,
                    },
                });
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_SKIP_FEE_HEADER_SELECTION,
                    },
                });
                setActiveStep(PROCESS_SLUGS?.PAYMENT_CARDS);
            } else {
                console.error('No match found! Please contact support@grayquest.com');
                toast.error('Oops! Something went wrong. Please contact support@grayquest.com');
            }

            let erpApiSetOptionsData = null;
            if (query.get('uid')) {
                // ERPI API Redirection Flow
                // fetch setOptions Data from session created by ERP API
                const erpApiSessionData = await fetchSessionData(query.get('uid'));
                erpApiSetOptionsData = erpApiSessionData?.data?.data?.session_data || null;
                if (
                    erpApiSetOptionsData?.customer_mobile &&
                    !hasDollarInString(erpApiSetOptionsData?.customer_mobile)
                ) {
                    handleDataUpdate({
                        ...(erpApiSetOptionsData?.customer_mobile && {
                            customer_mobile: erpApiSetOptionsData?.customer_mobile,
                        }),
                    });
                }
                if (erpApiSetOptionsData?.student_id) {
                    handleDataUpdate({
                        ...(erpApiSetOptionsData?.student_id && {
                            student_uuid: erpApiSetOptionsData?.student_id?.toString(),
                        }),
                    });
                }
            }
        } catch (error) {
            console.error('fetchInitalPageData error', error);
        } finally {
            setIsApiLoading(false);
        }
    };

    useEffect(() => {
        fetchInitalPageData();
    }, [])

    return (
        <div className={classes.PaymentSection} ref={paymentSectionRef}>
            {!isApiLoading ? 
                <>
                    {renderActiveStep(activeStep)}
                </>
            : <div className={classes.SpinnerLoaderWrapper}>
                <SpinnerLoader size='large' />
            </div>}
        </div>
    )
}

PaymentSection.propTypes = {}

export default PaymentSection;